import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import FileUpload, { FileUploadPresentationProps } from "@insite/mobius/FileUpload";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Img, { ImgProps } from "@insite/mobius/Img";
import Link from "@insite/mobius/Link";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomCustomerLogosState } from "@midland-industries/Store/Data/CustomerLogos/CustomerLogosReducer";
import deleteCurrentCustomerLogo from "@midland-industries/Store/Data/CustomerLogos/Handlers/DeleteCurrentCustomerLogo";
import loadCurrentCustomerLogo from "@midland-industries/Store/Data/CustomerLogos/Handlers/LoadCurrentCustomerLogo";
import saveCurrentCustomerLogo from "@midland-industries/Store/Data/CustomerLogos/Handlers/SaveCurrentCustomerLogo";
import React, { ReactNode, useContext, useEffect, useState } from "react";
import { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { AnyStyledComponent, css } from "styled-components";

const enum fields {
    maxImageSizeMB = "maxImageSizeMB",
    allowManageLogo = "allowManageLogo",
    showOuterBorder = "showOuterBorder",
    showInnerBorder = "showInnerBorder",
}

interface OwnProps {
    fields: {
        [fields.maxImageSizeMB]: number;
        [fields.allowManageLogo]: boolean;
        [fields.showOuterBorder]: boolean;
        [fields.showInnerBorder]: boolean;
    };
    onLogoImageUrlChange?: (logoImageUrl: string) => void;
}

const mapStateToProps = (state: CustomCustomerLogosState) => {
    const customLogosState = state.xcentiumCustomerLogos;
    const session = getSession(state);
    const billToId = session.billToId;
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        customLogosState,
        billToId,
        isAuthenticated,
    };
};

const mapDispatchToProps = {
    saveCurrentCustomerLogo,
    loadCurrentCustomerLogo,
    deleteCurrentCustomerLogo,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CustomerLogoStyles {
    wrapper: InjectableCss;
    logoOuterWrapper?: InjectableCss;
    logoInnerWrapper: InjectableCss;
    noLogoWrapper?: InjectableCss;
    noLogoMessage?: TypographyPresentationProps;
    headerWrapper?: InjectableCss;
    headerText?: TypographyPresentationProps;
    bodyWrapper?: InjectableCss;
    container?: GridContainerProps;
    detailsLabelGridItem?: GridItemProps;
    detailsLabelText?: TypographyPresentationProps;
    detailsValueGridItem?: GridItemProps;
    detailsValueText?: TypographyPresentationProps;
    logoWrapper?: InjectableCss;
    logoImageWrapper?: InjectableCss;
    logo?: ImgProps;
    actionLinksWrapper?: InjectableCss;
    addChangeLogoWrapper?: InjectableCss;
    addChangeLogoTitleWrapper: InjectableCss;
    addChangeLogoTitle?: TypographyPresentationProps;
    addChangeLogoForm?: InjectableCss;
    addChangeLogoNotesWrapper?: InjectableCss;
    addChangeLogoNotesInnerWrapper?: InjectableCss;
    addChangeLogoNotes?: InjectableCss;
    fileUploadWrapper?: InjectableCss;
    fileUpload?: FileUploadPresentationProps;
    addChangeLogoActionButtonsWrapper?: InjectableCss;
    addChangeLogoCancelButton?: ButtonPresentationProps;
    addChangeLogoSubmitButton?: ButtonPresentationProps;
    deleteLogoWrapper?: InjectableCss;
    deleteLogoTitleWrapper?: InjectableCss;
    deleteLogoTitle?: TypographyPresentationProps;
    deleteLogoActionButtonsWrapper?: InjectableCss;
    deleteLogoCancelButton?: ButtonPresentationProps;
    deleteLogoDeleteButton?: ButtonPresentationProps;
    notAuthWrapper?: InjectableCss;
    notAuthTypography?: TypographyPresentationProps;
}

export const customerLogoStyles: CustomerLogoStyles = {
    wrapper: {
        css: css`
            margin-bottom: 20px;
        `,
    },
    noLogoWrapper: {
        css: css`
            margin: 20px 0;
            width: 100%;
            display: flex;
            justify-content: center;
        `,
    },
    headerWrapper: {
        css: css`
            background-color: ${getColor("common.accent")};
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 10px;
        `,
    },
    headerText: {
        css: css`
            font-weight: 700;
            padding: 10px;
        `,
    },
    bodyWrapper: {
        css: css``,
    },
    container: {
        gap: 0,
    },
    detailsLabelGridItem: {
        width: 4,
    },
    detailsLabelText: {
        css: css`
            font-weight: 600;
            padding: 5px 0 5px 5px;
        `,
    },
    detailsValueGridItem: {
        width: 12,
    },
    detailsValueText: {
        css: css`
            padding: 5px 0;
        `,
    },
    logoOuterWrapper: {
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
        `,
    },
    logoInnerWrapper: {
        css: css`
            padding: 10px;
            margin-bottom: 20px;
            max-height: 200px;
            min-height: 20px;
            max-width: 200px;
            min-width: 20px;
        `,
    },
    logo: {
        css: css`
            width: 100%;
            height: auto;
            max-height: 100%;
        `,
    },
    actionLinksWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            margin: 0 10px 10px 10px;
            padding: 10px;
        `,
    },
    notAuthWrapper: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
    addChangeLogoTitleWrapper: {
        css: css`
            background-color: ${getColor("common.accent")};
            padding: 10px;
        `,
    },
    addChangeLogoTitle: {
        css: css`
            font-weight: 700;
            padding: 10px;
        `,
    },
    addChangeLogoForm: {
        css: css`
            margin: 10px 0;
            padding: 10px 0;
        `,
    },
    addChangeLogoNotesWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            padding: 10px 0;
        `,
    },
    addChangeLogoNotesInnerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
    fileUploadWrapper: {
        css: css`
            width: 100%;
            padding: 10px;
        `,
    },
    addChangeLogoActionButtonsWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            margin: 10px 0;
            padding: 10px 0;
        `,
    },
    addChangeLogoCancelButton: {
        variant: "secondary",
        css: css`
            margin-right: 15px;
        `,
    },
    addChangeLogoSubmitButton: {
        variant: "primary",
    },
    deleteLogoTitleWrapper: {
        css: css`
            background-color: ${getColor("common.accent")};
            border-top: 1px solid ${getColor("common.border")};
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 10px;
        `,
    },
    deleteLogoTitle: {
        css: css`
            font-weight: 700;
            padding: 10px;
        `,
    },
    deleteLogoActionButtonsWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            margin: 10px 0;
            padding: 10px 0;
        `,
    },
    deleteLogoCancelButton: {
        variant: "secondary",
        css: css`
            margin-right: 15px;
        `,
    },
    deleteLogoDeleteButton: {
        variant: "primary",
    },
};

const styles = customerLogoStyles;

export const CustomerLogo: FC<Props> = props => {
    const { customLogosState, billToId, isAuthenticated, fields } = props;
    const { loadCurrentCustomerLogo, saveCurrentCustomerLogo, deleteCurrentCustomerLogo } = props;
    const { maxImageSizeMB, allowManageLogo, showOuterBorder, showInnerBorder } = fields;

    const [logoImageUrl, setLogoImageUrl] = useState<string>("");
    const [showAddLogo, setShowAddLogo] = useState<boolean>(false);
    const [showDeleteLogo, setShowDeleteLogo] = useState<boolean>(false);

    const [fileName, setFileName] = useState<string>("");
    const [fileSize, setFileSize] = useState<number>(0);
    const [file, setFile] = useState<any>(null);

    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [logoError, setLogoError] = useState<ReactNode>("");

    const toasterContext = useContext(ToasterContext);

    useEffect(() => {
        if (isAuthenticated && !customLogosState.isLoaded && !customLogosState.isLoading) {
            loadCurrentCustomerLogo({ billToId });
        }
    }, []);

    useEffect(() => {
        setLogoImageUrl(customLogosState.logoImageUrl);
        if (props.onLogoImageUrlChange) {
            props.onLogoImageUrlChange(customLogosState.logoImageUrl);
        }
    }, [customLogosState]);

    const handleAddChangeClickEvent = (
        e: React.MouseEvent<Element, MouseEvent> | React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    ) => {
        setShowAddLogo(true);
    };

    const handleDeleteClickEvent = (
        e: React.MouseEvent<Element, MouseEvent> | React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    ) => {
        setShowDeleteLogo(true);
    };

    const handleCancelButtonEvent = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();

        resetForm();
    };

    const fileRequiredFieldMessage = siteMessage("CustomerLogos_Image_Required");
    const validateLogo = (fileName: string, fileSize: number) => {
        let errorMessage = !fileName ? (fileRequiredFieldMessage as string) : "";
        const fileSizeKB = fileSize / 1024;
        const fileSizeMB = fileSizeKB / 1024;
        if (fileSizeMB > maxImageSizeMB) {
            errorMessage = siteMessage("CustomerLogos_Image_TooLarge", maxImageSizeMB.toString()) as string;
        }
        setLogoError(errorMessage);
        return {
            isValid: !(errorMessage.length > 0),
            error: errorMessage,
        };
    };

    const handleFileUploadFileChangeEvent = (event: React.ChangeEvent<AnyStyledComponent>) => {
        const file = event.target.files[0];
        if (file) {
            validateLogo(file.name, file.size);
            setFileName(file.name);
            setFileSize(file.size);
            setFile(file);
        } else {
            // if the user is deleting the file, remove it
            setFileName("");
            setFileSize(0);
            setFile(null);
        }
    };

    const isFormValid = () => {
        const validateLogoResults = validateLogo(fileName, fileSize);

        return validateLogoResults.isValid;
    };

    const resetForm = () => {
        setIsSubmitting(false);
        setIsSubmitted(false);
        setShowAddLogo(false);
        setShowDeleteLogo(false);
        setLogoError("");
        setFileName("");
        setFileSize(0);
        setFile(null);
    };

    const handleFileUploadEvent = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();
        e.stopPropagation();

        setIsSubmitting(true);
        if (!isSubmitted) {
            setIsSubmitted(true);
        }

        if (!isFormValid()) {
            setIsSubmitting(false);
            return;
        }

        const fileExtensionParts = fileName.split(".");
        let fileExtension = "";
        if (fileExtensionParts.length > 1) {
            fileExtension = fileExtensionParts[fileExtensionParts.length - 1];
        }

        const formData = new FormData();

        formData.append("file", file);

        saveCurrentCustomerLogo({
            formData,
            onSuccess: response => {
                setIsSubmitted(false);
                setIsSubmitting(false);
                resetForm();
                toasterContext.addToast({
                    body: translate("Logo Updated Successfully"),
                    messageType: "success",
                });
            },
            onError: error => {
                setIsSubmitting(false);
                toasterContext.addToast({
                    body: translate("Logo Failed to Upload"),
                    messageType: "danger",
                });
            },
        });
    };

    const handleDeleteFileEvent = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();
        e.stopPropagation();

        deleteCurrentCustomerLogo({
            billToId,
            onSuccess: response => {
                resetForm();
                toasterContext.addToast({
                    body: translate("Logo Deleted Successfully"),
                    messageType: "success",
                });
            },
            onError: error => {
                toasterContext.addToast({
                    body: translate("Logo Failed to Delete"),
                    messageType: "danger",
                });
            },
        });
    };

    const StyledForm = getStyledWrapper("form");

    const displayActionLinks = allowManageLogo && !showAddLogo && !showDeleteLogo;
    const displayAddChangeLogo = showAddLogo;
    const displayDeleteLogo = showDeleteLogo;
    const hasLogo = !(logoImageUrl === "");

    const finalLogoInnerWrapperStyle: InjectableCss<{}> = {};
    if (showInnerBorder) {
        finalLogoInnerWrapperStyle.css = css`
            border: 1px solid ${getColor("common.border")};
            ${styles.logoInnerWrapper.css}
        `;
    } else {
        finalLogoInnerWrapperStyle.css = css`
            ${styles.logoInnerWrapper.css}
        `;
    }

    const logoElementsWhenHasLogo = (
        <StyledWrapper {...styles.logoOuterWrapper}>
            <StyledWrapper {...finalLogoInnerWrapperStyle}>
                <Img {...styles.logo} loading="eager" src={logoImageUrl} />
            </StyledWrapper>
        </StyledWrapper>
    );

    const logoElementsWhenHasNoLogo = (
        <StyledWrapper {...styles.noLogoWrapper}>
            <Typography {...styles.noLogoMessage}>{translate("No Logo on File")}</Typography>
        </StyledWrapper>
    );

    const actionButtonsWhenHasLogo = (
        <StyledWrapper {...styles.actionLinksWrapper}>
            <Link onClick={handleAddChangeClickEvent}>Change Logo</Link>
            <Typography>&nbsp;|&nbsp;</Typography>
            <Link onClick={handleDeleteClickEvent}>Remove Logo</Link>
        </StyledWrapper>
    );

    const actionButtonsWhenHasNoLogo = (
        <StyledWrapper {...styles.actionLinksWrapper}>
            <Link onClick={handleAddChangeClickEvent}>Add Logo</Link>
        </StyledWrapper>
    );

    const finalWrapperStyle: InjectableCss<{}> = {};
    const finalTitleWrapperStyle: InjectableCss<{}> = {};
    if (showOuterBorder) {
        finalWrapperStyle.css = css`
            border: 1px solid ${getColor("common.border")};
            padding-top: 20px;
            ${styles.wrapper.css}
        `;
        finalTitleWrapperStyle.css = css`
            border-top: 1px solid ${getColor("common.border")};
            border-bottom: 1px solid ${getColor("common.border")};
            ${styles.addChangeLogoTitleWrapper.css}
        `;
    } else {
        finalWrapperStyle.css = css`
            ${styles.wrapper.css}
        `;
        finalTitleWrapperStyle.css = css`
            border: 1px solid ${getColor("common.border")};
            ${styles.addChangeLogoTitleWrapper.css}
        `;
    }

    if (!hasLogo && !allowManageLogo) {
        return null;
    }

    return (
        <StyledWrapper {...finalWrapperStyle} data-test-selector="wrapper">
            {hasLogo && logoElementsWhenHasLogo}
            {!hasLogo && logoElementsWhenHasNoLogo}
            {displayActionLinks && hasLogo && actionButtonsWhenHasLogo}
            {displayActionLinks && !hasLogo && actionButtonsWhenHasNoLogo}
            {displayAddChangeLogo && (
                <StyledWrapper {...styles.addChangeLogoWrapper}>
                    <StyledWrapper {...finalTitleWrapperStyle}>
                        <Typography {...styles.addChangeLogoTitle}>{translate("Upload New Company Logo")}</Typography>
                    </StyledWrapper>
                    <StyledForm {...styles.addChangeLogoForm}>
                        <StyledWrapper {...styles.fileUploadWrapper}>
                            <FileUpload
                                {...styles.fileUpload}
                                error={isSubmitted && logoError}
                                width="100%"
                                fileName={fileName || ""}
                                accept=".jpeg, .png, .gif, .webp"
                                onFileChange={event => handleFileUploadFileChangeEvent(event)}
                                required
                                placeholder="No file chosen"
                            />
                        </StyledWrapper>
                        <StyledWrapper {...styles.addChangeLogoActionButtonsWrapper}>
                            <Button
                                {...styles.addChangeLogoCancelButton}
                                disabled={isSubmitting}
                                onClick={e => {
                                    handleCancelButtonEvent(e);
                                }}
                            >
                                {translate("Cancel")}
                            </Button>
                            <Button
                                {...styles.addChangeLogoSubmitButton}
                                disabled={isSubmitting}
                                onClick={e => {
                                    handleFileUploadEvent(e);
                                }}
                            >
                                {translate("Submit")}
                            </Button>
                        </StyledWrapper>
                    </StyledForm>
                    <StyledWrapper {...finalTitleWrapperStyle}>
                        <Typography {...styles.addChangeLogoTitle}>{translate("Upload Instructions")}</Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.addChangeLogoNotesWrapper}>
                        <StyledWrapper {...styles.addChangeLogoNotesInnerWrapper}>
                            <Typography>{translate(`Recommended size (200x125) (${maxImageSizeMB}MB Max)`)}</Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.addChangeLogoNotesInnerWrapper}>
                            <Typography>{translate(`Supported Image Formats: JPEG, PNG, GIF, or WEBP`)}</Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            )}
            {displayDeleteLogo && (
                <StyledWrapper {...styles.deleteLogoWrapper}>
                    <StyledWrapper {...finalTitleWrapperStyle}>
                        <Typography {...styles.deleteLogoTitle}>{translate("Confirm Delete Current Logo")}</Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.deleteLogoActionButtonsWrapper}>
                        <Button
                            {...styles.deleteLogoCancelButton}
                            disabled={isSubmitting}
                            onClick={e => {
                                handleCancelButtonEvent(e);
                            }}
                        >
                            {translate("Cancel")}
                        </Button>
                        <Button
                            {...styles.deleteLogoDeleteButton}
                            disabled={isSubmitting}
                            onClick={e => {
                                handleDeleteFileEvent(e);
                            }}
                        >
                            {translate("Delete")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export const MyCustomerLogo = connect(mapStateToProps, mapDispatchToProps)(CustomerLogo);

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CustomerLogo),
    definition: {
        group: "Basic",
        icon: "image",
        displayName: "Customer Logo",
        fieldDefinitions: [
            {
                name: fields.maxImageSizeMB,
                displayName: "Max Image Size (MB)",
                editorTemplate: "IntegerField",
                defaultValue: 1,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.allowManageLogo,
                displayName: "Display Logo Management Links",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tooltip: "Allows the user to add, change or remove the current Logo using this Widget.",
            },
            {
                name: fields.showOuterBorder,
                displayName: "Show Outer Border",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tooltip: "Displays a thin border around the outer boundry of the widget.",
            },
            {
                name: fields.showInnerBorder,
                displayName: "Show Inner Border",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tooltip: "Displays a thin border around the outer boundry of the logo.",
            },
        ],
    },
};

export default widgetModule;
